import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Landmark, TrendingUp, ArrowRight, 
  Shield, ChartBar, Wallet, HandCoins
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const HomePage = () => {
  const [stats, setStats] = useState({
    total_members: 0,
    total_fund_pool: 0,
    active_loans: 0,
    total_profit: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/public/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const statCards = [
    { label: 'Total Members', value: stats.total_members, icon: Users, color: '#0A2540' },
    { label: 'Total Fund Pool', value: formatCurrency(stats.total_fund_pool), icon: Landmark, color: '#00C853' },
    { label: 'Active Loans', value: stats.active_loans, icon: Wallet, color: '#2962FF' },
    { label: 'Total Profit', value: formatCurrency(stats.total_profit), icon: TrendingUp, color: '#FF9800' }
  ];

  const features = [
    { title: 'Member Management', description: 'Complete member lifecycle management with capital tracking', icon: Users },
    { title: 'Loan System', description: 'Transparent loan approvals with automated EMI calculation', icon: Wallet },
    { title: 'Contribution Tracking', description: 'Monthly ₹1000 contributions with penalty management', icon: HandCoins },
    { title: 'Profit Distribution', description: 'Fair profit sharing from interest and penalties', icon: ChartBar }
  ];

  return (
    <div className="min-h-screen bg-[#F4F7FB]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0A2540] flex items-center justify-center">
              <Landmark className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-[#0A2540] text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Shri Bhavani SHG
            </span>
          </div>
          <Link 
            to="/login"
            data-testid="nav-login-btn"
            className="bg-[#0A2540] text-white px-6 py-2.5 rounded-xl font-medium hover:bg-[#163A5F] transition-all shadow-lg hover:shadow-xl"
          >
            Login
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#163A5F]">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-[#00C853]/20 text-[#00C853] text-sm font-medium mb-6">
              Self Help Group
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Shri Bhavani
              <br />
              <span className="text-[#00C853]">Self Help Group</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-xl">
              Empowering Members Through Smart Financial Support. Building wealth together, one contribution at a time.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/login"
                data-testid="hero-login-btn"
                className="inline-flex items-center gap-2 bg-[#00C853] text-white px-8 py-4 rounded-xl font-medium hover:bg-[#009624] transition-all shadow-lg hover:shadow-xl"
              >
                Get Started <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-4 rounded-xl font-medium hover:bg-white/20 transition-all backdrop-blur"
              >
                Learn More
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 -mt-8 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`stat-card-${index}`}
                className="glass-panel p-6 hover:shadow-xl transition-all"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${stat.color}15` }}
                >
                  <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                </div>
                <p className="text-sm text-[#425466] mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {loading ? (
                    <span className="inline-block w-24 h-8 skeleton rounded"></span>
                  ) : (
                    stat.value
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-[#2962FF]/10 text-[#2962FF] text-sm font-medium mb-4">
              Features
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Everything You Need
            </h2>
            <p className="text-[#425466] max-w-2xl mx-auto">
              A complete financial management system designed for self-help groups
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="dashboard-card p-8 flex gap-6"
              >
                <div className="w-14 h-14 rounded-xl bg-[#0A2540]/5 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-7 h-7 text-[#0A2540]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0A2540] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-[#425466]">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-[#00C853]/10 text-[#00C853] text-sm font-medium mb-4">
                Security & Trust
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Built on Trust & Transparency
              </h2>
              <p className="text-[#425466] mb-8 text-lg">
                Every transaction is recorded, every decision is transparent. Our platform ensures complete accountability for all members.
              </p>
              <div className="space-y-4">
                {[
                  'Complete transaction history',
                  'Automated EMI calculations',
                  'Fair ticket system for fund utilization',
                  'Real-time profit tracking'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00C853] flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-[#0A2540]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/group-photo.jpg" 
                  alt="Shri Bhavani Self Help Group Members"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 glass-panel p-6 max-w-xs">
                <div className="flex items-center gap-4 mb-3">
                  <Shield className="w-10 h-10 text-[#00C853]" />
                  <div>
                    <p className="font-semibold text-[#0A2540]">100% Secure</p>
                    <p className="text-sm text-[#425466]">Your data is protected</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="glass-panel p-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Ready to Get Started?
            </h2>
            <p className="text-[#425466] mb-8 text-lg">
              Join Shri Bhavani Self Help Group today and be part of our growing financial community.
            </p>
            <Link
              to="/login"
              data-testid="cta-login-btn"
              className="inline-flex items-center gap-2 bg-[#0A2540] text-white px-8 py-4 rounded-xl font-medium hover:bg-[#163A5F] transition-all shadow-lg hover:shadow-xl"
            >
              Login Now <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2540] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <Landmark className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-white text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Shri Bhavani Self Help Group
              </span>
            </div>
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Shri Bhavani SHG. All rights reserved.
            </p>
          </div>
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-gray-400 text-sm">
              Designed By: <span className="text-white font-semibold">Viral Boostup</span> | 
              <a href="tel:+919110278059" className="text-[#00C853] hover:underline ml-1">+91 9110278059</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
